// ============================================
// 오링왕국 - 상수 정의
// ============================================

import { Building, BuildingType, Era, Character, CharacterId, MiningUpgrade, MiningUpgradeType } from "./types";

// ============================================
// 게임 설정
// ============================================

export const TICK_RATE_MS = 1000;  // 1초당 1틱
export const EVENT_CHANCE_PER_TICK = 0.015;  // 1.5% 확률 (약 66초당 1회)
export const SEASON_LENGTH = 30;  // 30턴 = 1계절
export const CHAIN_EVENT_DELAY = 5;  // 체인 이벤트 사이 최소 턴
export const SAVE_KEY = 'oring_kingdom_save';

// ============================================
// 초기 건물 데이터
// ============================================

export const INITIAL_BUILDINGS: Record<BuildingType, Building> = {
    [BuildingType.TENT]: {
        id: BuildingType.TENT,
        name: "지푸라기 둥지",
        description: "오리들이 옹기종기 모여 자는 따뜻한 보금자리입니다.",
        baseCost: 10,
        costMultiplier: 1.15,
        incomePerTick: 1,
        happinessBonus: 1,
        populationBonus: 2,
        defenseBonus: 0,
        unlockEra: Era.POND,
        icon: "🪺",
        count: 0,
        maxCount: 30
    },
    [BuildingType.FARM]: {
        id: BuildingType.FARM,
        name: "지렁이 농장",
        description: "통통한 지렁이를 길러 오리들의 배를 채웁니다.",
        baseCost: 80,
        costMultiplier: 1.18,
        incomePerTick: 5,
        happinessBonus: 2,
        populationBonus: 0,
        defenseBonus: 0,
        unlockEra: Era.POND,
        icon: "🪱",
        count: 0,
        maxCount: 25
    },
    [BuildingType.MINE]: {
        id: BuildingType.MINE,
        name: "반짝이는 연못",
        description: "연못 바닥에서 반짝이는 오링(도넛?)을 건져냅니다.",
        baseCost: 400,
        costMultiplier: 1.2,
        incomePerTick: 20,
        happinessBonus: 0,
        populationBonus: 0,
        defenseBonus: 0,
        unlockEra: Era.VILLAGE,
        icon: "🌊",
        count: 0,
        maxCount: 15
    },
    [BuildingType.BARRACKS]: {
        id: BuildingType.BARRACKS,
        name: "거위 경비대",
        description: "사나운 거위들을 고용해 왕국의 치안을 담당합니다. 위기 상황에 피해를 줄여줍니다.",
        baseCost: 1000,
        costMultiplier: 1.25,
        incomePerTick: 0,
        happinessBonus: 3,
        populationBonus: 0,
        defenseBonus: 10,
        unlockEra: Era.VILLAGE,
        icon: "🪿",
        count: 0,
        maxCount: 10
    },
    [BuildingType.SCHOOL]: {
        id: BuildingType.SCHOOL,
        name: "꽥꽥 학교",
        description: "어린 오리들에게 지혜를 가르칩니다. 특수 이벤트가 발생합니다.",
        baseCost: 600,
        costMultiplier: 1.3,
        incomePerTick: 2,
        happinessBonus: 5,
        populationBonus: 3,
        defenseBonus: 0,
        unlockEra: Era.VILLAGE,
        icon: "📚",
        count: 0,
        maxCount: 10
    },
    [BuildingType.HOSPITAL]: {
        id: BuildingType.HOSPITAL,
        name: "치유의 둥지",
        description: "아픈 오리들을 치료합니다. 비용과 인구 보너스가 2배씩 증가!",
        baseCost: 1000000,  // 100만
        costMultiplier: 2.0,  // 100만 → 200만 → 400만 → 800만...
        incomePerTick: 10,
        happinessBonus: 8,
        populationBonus: 5,  // 5 → 10 → 20 → 40... (배수 증가)
        defenseBonus: 5,
        unlockEra: Era.CITY,
        icon: "🏥",
        count: 0,
        maxCount: 10
    },
    [BuildingType.MARKET]: {
        id: BuildingType.MARKET,
        name: "오링 시장",
        description: "활발한 거래가 이루어지는 시장입니다. 비용과 수입이 2배씩 증가!",
        baseCost: 5000000,  // 500만
        costMultiplier: 2.0,  // 500만 → 1000만 → 2000만...
        incomePerTick: 100,  // 100 → 200 → 400... (배수 증가)
        happinessBonus: 5,
        populationBonus: 0,
        defenseBonus: 0,
        unlockEra: Era.CITY,
        icon: "🏪",
        count: 0,
        maxCount: 15
    },
    [BuildingType.CASTLE]: {
        id: BuildingType.CASTLE,
        name: "황금 오리 궁전",
        description: "왕국의 위엄을 보여주는 거대한 둥지입니다. 비용과 보너스 2배씩 증가!",
        baseCost: 20000000,  // 2000만
        costMultiplier: 2.0,  // 2000만 → 4000만 → 8000만
        incomePerTick: 200,
        happinessBonus: 10,
        populationBonus: 10,  // 10 → 20 → 40 (배수 증가)
        defenseBonus: 20,
        unlockEra: Era.KINGDOM,
        icon: "🏰",
        count: 0,
        maxCount: 3
    },
    [BuildingType.HARBOR]: {
        id: BuildingType.HARBOR,
        name: "오링 항구",
        description: "먼 바다와 연결되는 항구입니다. 비용과 보너스 2배씩 증가!",
        baseCost: 30000000,  // 3000만
        costMultiplier: 2.0,  // 3000만 → 6000만 → 1억2천...
        incomePerTick: 150,
        happinessBonus: 5,
        populationBonus: 5,  // 5 → 10 → 20... (배수 증가)
        defenseBonus: 5,
        unlockEra: Era.KINGDOM,
        icon: "⚓",
        count: 0,
        maxCount: 5
    },
    [BuildingType.TEMPLE]: {
        id: BuildingType.TEMPLE,
        name: "황금알 신전",
        description: "전설의 황금알을 모시는 신전입니다. 비용과 수입 2배씩 증가!",
        baseCost: 50000000,  // 5000만
        costMultiplier: 2.0,  // 5000만 → 1억
        incomePerTick: 500,  // 500 → 1000 (배수 증가)
        happinessBonus: 20,
        populationBonus: 10,
        defenseBonus: 10,
        unlockEra: Era.KINGDOM,
        icon: "🥚",
        count: 0,
        maxCount: 2
    },
    [BuildingType.WONDER]: {
        id: BuildingType.WONDER,
        name: "세계의 불가사의",
        description: "전설의 건축물입니다. 최고의 보너스와 특별한 엔딩!",
        baseCost: 500000000,  // 5억
        costMultiplier: 2.0,
        incomePerTick: 5000,
        happinessBonus: 50,
        populationBonus: 100,
        defenseBonus: 50,
        unlockEra: Era.EMPIRE,
        icon: "🗿",
        count: 0,
        maxCount: 1
    }
};

// ============================================
// 캐릭터 초기 데이터
// ============================================

export const INITIAL_CHARACTERS: Record<CharacterId, Character> = {
    sage_duck: {
        id: 'sage_duck',
        name: '현자 오리',
        title: '연못의 현인',
        description: '수백 년을 살아온 지혜로운 오리. 고대의 비밀을 알고 있습니다.',
        icon: '🦆🧙',
        relationship: 0,
        isUnlocked: false
    },
    merchant_goose: {
        id: 'merchant_goose',
        name: '상인 거위',
        title: '황금 거위 상회 대표',
        description: '어디서든 이익을 찾아내는 천재 상인.',
        icon: '🪿💰',
        relationship: 0,
        isUnlocked: false
    },
    fox_general: {
        id: 'fox_general',
        name: '여우 대장',
        title: '붉은 발톱 군단장',
        description: '여우 군단을 이끄는 위협적인 존재. 협상의 여지가 있을까?',
        icon: '🦊⚔️',
        relationship: -50,
        isUnlocked: false
    },
    migrant_bird: {
        id: 'migrant_bird',
        name: '철새 여행자',
        title: '하늘의 방랑자',
        description: '세계 각지를 여행하며 이야기를 전하는 철새.',
        icon: '🐦✈️',
        relationship: 0,
        isUnlocked: false
    },
    mystic_swan: {
        id: 'mystic_swan',
        name: '신비한 백조',
        title: '달빛 호수의 수호자',
        description: '신비로운 힘을 가진 전설의 백조. 예언의 능력이 있다고 합니다.',
        icon: '🦢✨',
        relationship: 0,
        isUnlocked: false
    },
    pirate_pelican: {
        id: 'pirate_pelican',
        name: '해적 펠리컨',
        title: '검은 파도의 선장',
        description: '바다의 무법자. 위험하지만 유용한 정보를 가지고 있습니다.',
        icon: '🐦‍⬛🏴‍☠️',
        relationship: 0,
        isUnlocked: false
    },
    royal_peacock: {
        id: 'royal_peacock',
        name: '왕족 공작',
        title: '무지개 왕국의 왕자',
        description: '화려한 공작 왕국의 왕자. 정략 결혼의 대상?',
        icon: '🦚👑',
        relationship: 0,
        isUnlocked: false
    },
    shadow_crow: {
        id: 'shadow_crow',
        name: '그림자 까마귀',
        title: '어둠의 정보상',
        description: '모든 비밀을 알고 있는 수상한 까마귀. 대가 없이는 아무것도 알려주지 않습니다.',
        icon: '🐦‍⬛🌑',
        relationship: 0,
        isUnlocked: false
    },
    ancient_turtle: {
        id: 'ancient_turtle',
        name: '고대 거북',
        title: '천년의 기억',
        description: '태초부터 살아온 거북. 이 세계의 비밀을 알고 있습니다.',
        icon: '🐢📜',
        relationship: 0,
        isUnlocked: false
    },
    rebel_sparrow: {
        id: 'rebel_sparrow',
        name: '반란군 참새',
        title: '자유의 투사',
        description: '불의에 저항하는 혁명가 참새. 민중의 친구.',
        icon: '🐦🔥',
        relationship: 0,
        isUnlocked: false
    }
};

// ============================================
// 시대 요구사항
// ============================================

export const ERA_REQUIREMENTS: Record<Era, { population: number; money: number; name: string }> = {
    [Era.POND]: { population: 0, money: 0, name: '작은 연못' },
    [Era.VILLAGE]: { population: 50, money: 1000, name: '오리 마을' },
    [Era.CITY]: { population: 200, money: 10000, name: '오링 도시' },
    [Era.KINGDOM]: { population: 500, money: 100000, name: '오링 왕국' },
    [Era.EMPIRE]: { population: 1000, money: 1000000, name: '오링 제국' }
};

// ============================================
// 계절 효과
// ============================================

export const SEASON_EFFECTS = {
    [Era.POND]: {
        SPRING: { income: 1.0, happiness: 1.1 },
        SUMMER: { income: 1.2, happiness: 1.2 },
        AUTUMN: { income: 1.3, happiness: 1.0 },
        WINTER: { income: 0.7, happiness: 0.8 }
    }
};

// ============================================
// 팁 메시지
// ============================================

export const TIPS = [
    "💡 행복도가 높으면 수입이 증가합니다!",
    "💡 거위 경비대는 위기 상황의 피해를 줄여줍니다.",
    "💡 계절마다 특별한 이벤트가 발생합니다.",
    "💡 캐릭터를 만나면 새로운 스토리가 열립니다.",
    "💡 여러 엔딩이 있습니다. 다양한 선택을 해보세요!",
    "💡 MBTI는 당신의 선택에 따라 결정됩니다.",
    "💡 체인 이벤트는 연속으로 진행되는 큰 스토리입니다.",
    "💡 신전을 지으면 신비한 이벤트가 발생합니다.",
    "💡 항구를 지으면 바다 모험이 가능해집니다.",
    "💡 황금알 전설을 들으면 특별한 일이...",
    "💡 여우와 친해질 수도 있습니다... 아마도?",
    "💡 겨울을 대비해 자원을 비축하세요!",
    "💡 10개의 체인을 완료하면 전설이 됩니다.",
    "💡 숨겨진 엔딩이 3개 있습니다!",
    "💡 너무 빠르게 채굴하면 미니게임이 나타납니다!",
    "💡 채굴 업그레이드로 효율을 높이세요!"
];

// ============================================
// 채굴 업그레이드 초기 데이터
// ============================================

export const INITIAL_MINING_UPGRADES: Record<MiningUpgradeType, MiningUpgrade> = {
    [MiningUpgradeType.GOLDEN_BEAK]: {
        id: MiningUpgradeType.GOLDEN_BEAK,
        name: "황금 부리",
        description: "반짝이는 황금 부리로 오링을 더 쉽게 모읍니다.",
        icon: "🐥",
        baseCost: 50,
        costMultiplier: 1.8,
        level: 0,
        maxLevel: 20,
        effect: { clickBonus: 1 }
    },
    [MiningUpgradeType.DIVING_GOGGLES]: {
        id: MiningUpgradeType.DIVING_GOGGLES,
        name: "잠수 고글",
        description: "깊은 물 속의 오링도 찾을 수 있습니다.",
        icon: "🥽",
        baseCost: 200,
        costMultiplier: 2.0,
        level: 0,
        maxLevel: 15,
        effect: { clickBonus: 2 }
    },
    [MiningUpgradeType.TREASURE_MAP]: {
        id: MiningUpgradeType.TREASURE_MAP,
        name: "보물 지도",
        description: "숨겨진 오링 광맥의 위치를 알려줍니다.",
        icon: "🗺️",
        baseCost: 1000,
        costMultiplier: 2.2,
        level: 0,
        maxLevel: 10,
        effect: { clickBonus: 5 }
    },
    [MiningUpgradeType.MAGIC_WAND]: {
        id: MiningUpgradeType.MAGIC_WAND,
        name: "마법 지팡이",
        description: "마법의 힘으로 오링 수확량이 2배가 됩니다.",
        icon: "🪄",
        baseCost: 5000,
        costMultiplier: 3.0,
        level: 0,
        maxLevel: 5,
        effect: { clickMultiplier: 2 }
    },
    [MiningUpgradeType.AUTO_FISHER]: {
        id: MiningUpgradeType.AUTO_FISHER,
        name: "자동 낚시꾼",
        description: "물가에 앉아 자동으로 오링을 낚아올립니다.",
        icon: "🎣",
        baseCost: 500,
        costMultiplier: 2.5,
        level: 0,
        maxLevel: 20,
        effect: { autoMining: 1 }
    },
    [MiningUpgradeType.LUCKY_CLOVER]: {
        id: MiningUpgradeType.LUCKY_CLOVER,
        name: "행운의 클로버",
        description: "가끔 2배의 오링을 얻습니다!",
        icon: "🍀",
        baseCost: 800,
        costMultiplier: 2.0,
        level: 0,
        maxLevel: 10,
        effect: { luckyChance: 0.05 }
    },
    [MiningUpgradeType.DUCK_ARMY]: {
        id: MiningUpgradeType.DUCK_ARMY,
        name: "오리 군단",
        description: "작은 오리들이 열심히 오링을 모아옵니다.",
        icon: "🐤🐤🐤",
        baseCost: 3000,
        costMultiplier: 2.8,
        level: 0,
        maxLevel: 10,
        effect: { autoMining: 5 }
    },
    [MiningUpgradeType.GOLDEN_EGG]: {
        id: MiningUpgradeType.GOLDEN_EGG,
        name: "황금알",
        description: "전설의 황금알! 모든 채굴 효율이 극대화됩니다.",
        icon: "🥚✨",
        baseCost: 50000,
        costMultiplier: 5.0,
        level: 0,
        maxLevel: 3,
        effect: { clickMultiplier: 3, autoMining: 10 }
    }
};

// ============================================
// 미니게임 설정
// ============================================

export const MINIGAME_TRIGGER_THRESHOLD = 5000; // 3분 내 이 만큼 채굴하면
export const MINIGAME_TRIGGER_WINDOW = 180000;  // 3분 (밀리초)
export const MINIGAME_MIN_CLICKS = 50;  // 최소 50회 클릭 필요

export const HIDDEN_PICTURE_EMOJIS = ['🦆', '🐥', '🍩', '🥚', '🌊', '🪿', '🐦', '🦢'];
export const PUZZLE_EMOJIS = ['🦆', '👑', '💰', '🏰', '🌸', '⭐', '🎀', '🌈', '🍀'];

export const MINIGAME_REWARDS = {
    hidden_picture: { base: 500, perDifficulty: 300 },
    puzzle: { base: 800, perDifficulty: 500 },
    memory: { base: 600, perDifficulty: 400 },
    catch_duck: { base: 300, perDifficulty: 200 }
};
